import { SelectV2, TooltipV2, SelectV2Option } from '@cimplico/components';
import { Ref, useEffect, useMemo, useState } from 'react';
import { Entity } from '../../types/entity';
import { useWorkpapers } from '../../queries/use-workpapers';
import { WorkpaperStatusEnum } from '../../types/workpaper';
import { formatDate } from '../../utilities/formatting';

type WorkpaperSelectProps = {
    entity?: Entity;
    onChange: (priorWorkpaperId: string | null) => void;
    onSelectWorkpaper?: (workpaper: { periodEnd: string } | null) => void;
    innerRef?: Ref<HTMLInputElement>;
    value?: string;
    placeholder?: string;
    disabledId?: string;
    label?: string;
    disabled?: boolean;
    currentPeriodEnd?: Date;
};

function WorkpaperSelect({
    entity,
    onChange,
    innerRef,
    value,
    placeholder,
    onSelectWorkpaper,
    disabledId,
    label,
    disabled,
    currentPeriodEnd,
}: WorkpaperSelectProps): JSX.Element {
    const [workpaperSearch, setWorkpaperSearch] = useState('');

    function handleWorkpaperChange(options: SelectV2Option[]): void {
        if (options.length === 0) {
            onChange(null);
            if (onSelectWorkpaper) onSelectWorkpaper(null);
        } else {
            const selectedId = options[0].value as string;
            onChange(selectedId);
            if (onSelectWorkpaper) {
                const selectedWorkpaper = workpapersQuery.data?.workpapers.find((wp) => wp.id === selectedId);
                onSelectWorkpaper(selectedWorkpaper || null);
            }
        }
    }

    const filter = entity ? `status=${WorkpaperStatusEnum.ACTIVE}&status=${WorkpaperStatusEnum.COMPLETE}&entity=${entity.id}` : '';

    const workpapersQuery = useWorkpapers({
        page: 1,
        limit: 1000,
        filter,
        search: workpaperSearch,
        sort: '',
        enabled: Boolean(entity),
    });

    const workpapers = useMemo(() => {
        return (
            (workpapersQuery.data?.workpapers || [])
                .sort((a, b) => new Date(a.periodEnd).getTime() - new Date(b.periodEnd).getTime())
                .map((wp) => {
                    return {
                        value: wp.id,
                        label: wp.name,
                        isSelected: wp.id === value,
                        description: formatDate(wp.periodEnd),
                        isDisabled: wp.id === disabledId || (currentPeriodEnd && new Date(wp.periodEnd) >= new Date(currentPeriodEnd)),
                    };
                }) || []
        );
    }, [workpapersQuery.data?.workpapers, value, disabledId, currentPeriodEnd]);

    const [selectedOption, setSelected] = useState<SelectV2Option | undefined>(workpapers.find((option) => option.value === value));

    useEffect(() => {
        setSelected(workpapers.find((option) => option.value === value));
    }, [value, workpapers]);

    return (
        <TooltipV2 message="Please select an entity" disabled={!!entity}>
            <SelectV2
                title={label}
                disabled={!entity || disabled}
                placeholder={placeholder || 'Select a workpaper...'}
                onChange={(options): void => {
                    handleWorkpaperChange(options);
                }}
                isLoading={workpapersQuery.isFetching || workpapersQuery.isLoading}
                options={workpapers}
                value={selectedOption ? [selectedOption] : []}
                isOptional={false}
                onSearch={setWorkpaperSearch}
                ref={innerRef}
                keepSelectedOptions={true}
            />
        </TooltipV2>
    );
}

export default WorkpaperSelect;
